import { hatcheryDropdownModes } from "@/app/theme";
import { MultiSelectDropdown, SingleSelectDropdown } from "@carbon6/c6-frontend-design";
import { useSearchParams } from "react-router";
import { FilterBar } from "@/components/filter";
import { workerJobStatusLabel } from "./workerJobStatusLabel";
import { diagnosticJobKindLabel } from "./diagnosticJobKindLabel";
import {
  readWorkerJobFilters,
  uploadJobStatuses,
  diagnosticJobKinds,
  diagnosticJobStatuses,
} from "./workerJobFilters";

export function WorkerJobFilterBar({ diagnostic }: { diagnostic: boolean }) {
  const [params, setParams] = useSearchParams();
  const statuses: readonly string[] = diagnostic ? diagnosticJobStatuses : uploadJobStatuses;
  const filters = readWorkerJobFilters(params, statuses);
  const change = (key: string, values: readonly string[]) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        next.delete("page");
        values.forEach((value) => next.append(key, value));
        return next;
      },
      { replace: true },
    );
  const clear = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ["status", "kind", "order", "page", "period"].forEach((key) => next.delete(key));
        return next;
      },
      { replace: true },
    );
  const chips = [
    ...(filters.period !== "24h"
      ? [
          {
            key: "period",
            label: filters.period === "7d" ? "최근 7일" : "전체 기간",
            removeLabel: "조회 기간 초기화",
            onRemove: () => change("period", []),
          },
        ]
      : []),
    ...filters.status.map((status) => ({
      key: status,
      label: workerJobStatusLabel(status),
      removeLabel: `${workerJobStatusLabel(status)} 해제`,
      onRemove: () =>
        change(
          "status",
          filters.status.filter((value) => value !== status),
        ),
    })),
    ...(diagnostic && filters.kind
      ? [
          {
            key: "kind",
            label: diagnosticJobKindLabel(filters.kind),
            removeLabel: "종류 해제",
            onRemove: () => change("kind", []),
          },
        ]
      : []),
  ];
  return (
    <FilterBar ariaLabel="작업 큐 필터" chips={chips} onClearAll={clear}>
      <SingleSelectDropdown
        label="조회 기간"
        mode={hatcheryDropdownModes.filter}
        value={filters.period}
        neutralValue="24h"
        options={[
          { value: "24h", label: "최근 24시간" },
          { value: "7d", label: "최근 7일" },
          { value: "all", label: "전체 기간" },
        ]}
        onValueChange={(value) => change("period", value === "24h" ? [] : [value])}
      />
      <MultiSelectDropdown
        label="상태"
        mode={hatcheryDropdownModes.filter}
        options={statuses.map((value) => ({ value, label: workerJobStatusLabel(value) }))}
        selectedValues={filters.status}
        onSelectionChange={(values) => change("status", values)}
        placeholder="전체"
      />
      {diagnostic && (
        <SingleSelectDropdown
          label="종류"
          mode={hatcheryDropdownModes.filter}
          neutralValue="all"
          value={filters.kind ?? "all"}
          options={[
            { value: "all", label: "전체" },
            ...diagnosticJobKinds.map((kind) => ({
              value: kind,
              label: diagnosticJobKindLabel(kind),
            })),
          ]}
          onValueChange={(value) => change("kind", value === "all" ? [] : [value])}
        />
      )}
    </FilterBar>
  );
}
